import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import Swal from 'sweetalert2';
import { Configuration } from '../../../assets/data/config';
import imgUrl from '../../../assets/images/imgUrl';
import { useAuth } from '../../../hooks/useAuth';

const AdminDashboardHeader = ({ onDrawerToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Get admin data from localStorage
  const adminDataStr = localStorage.getItem('adminData');
  const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
  const usertype = adminData?.usertype;
  const { logout, user } = useAuth();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Logout',
        text: 'Are you sure you want to logout?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, logout!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) {
        
        return;
      }

      // Show loading
      Swal.fire({
        title: 'Logging out...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Call logout API
      const response = await fetch(`${Configuration.apiService.key}admin/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Clear admin data

        await logout();

        if (usertype === 'Entity') {
          localStorage.setItem('logoutUsertype', 'Entity');
        } else {
          localStorage.removeItem('logoutUsertype');
        }
        
        localStorage.removeItem('adminData');
        localStorage.removeItem('isAdminLoggedIn');
        
        // Close loading and redirect
        Swal.close();
        if (usertype === 'Entity') {
          navigate('/login');
        } else {
          navigate('/adminlogin');
        }
      } else {
        throw new Error('Logout API failed');
      }
    } catch (error) {
      await logout();
      console.error('Logout error:', error);
      
      // Close loading dialog if open
      Swal.close();
      
      // Even if API fails, clear local data and redirect

      if (usertype === 'Entity') {
          localStorage.setItem('logoutUsertype', 'Entity');
        } else {
          localStorage.removeItem('logoutUsertype');
        }

      localStorage.removeItem('adminData');
      localStorage.removeItem('isAdminLoggedIn');
      
      if (usertype === 'Entity') {
          navigate('/login');
        } else {
          navigate('/adminlogin');
        }
      
    }
    
    // Close the menu
    handleProfileMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: '100%' },
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, #3A8139 0%, #214F7D 100%)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        
        {/* Responsive Logo Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-start' },
            flexGrow: { xs: 1, sm: 0 },
            mr: { xs: 0, sm: 3 },
            position: { xs: 'relative', sm: 'static' },
            left: { xs: '0', sm: 'auto' },
            transform: { xs: 'none', sm: 'none' },
            zIndex: 1
          }}
        >
          <img 
            src={imgUrl.peaceMovImg} 
            alt="PeaceKeeper Movement"
            className="dashboardPeaceLogo"
            onClick={() => window.open('/', '_blank')}
            style={{ 
              cursor: 'pointer',
              maxWidth: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
              height: 'auto',
              maxHeight: '40px',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Admin Panel Title - Hidden on mobile to save space */}
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 600,
            display: { xs: 'none', sm: 'block' }
          }}
        >
          Admin Panel
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* User Info - Hidden on very small screens */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 200 }}>
              {adminData?.full_name || adminData?.username || 'Admin User'}, {adminData?.role || 'Admin'}
            </Typography>
          </Box>
          
          {/* Logout Button - Always visible */}
          <IconButton
            onClick={handleLogout}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
            aria-label="logout"
          >
            <Avatar sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.3)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)'
              }
            }}>
              <LogoutIcon />
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminDashboardHeader;
